/**
 * Asset Management — STAFF dashboard (default /app/assetmanagement).
 * "What's mine?" — my assigned assets, scan-to-find, and transfers awaiting my
 * acknowledgement. Admins additionally get a link to the org Overview.
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Stack, Group, Title, Text, Button, SimpleGrid, Card, ThemeIcon, ActionIcon, Tooltip,
  TextInput, Loader, Center, EmptyState, Divider, Badge,
} from '@/components/ui';
import { FadeIn } from '@/components/animations';
import {
  IconBox, IconCircleCheck, IconTool, IconBellRinging, IconScan, IconRefresh, IconArrowRight, IconLayoutDashboard,
} from '@tabler/icons-react';
import { useAssetService, useAssetScanService, type AssetModelView, type AssetAssignmentModelView } from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { toast } from '@/components/ui/providers/AlertProvider';
import { AssetStatusBadge } from '../components/AssetStatusBadge';
import { assetKeys, ASSET_FLAGS, ASSET_ADMIN_ROLES } from '../components/constants';

function StatCard({ label, value, icon, color, onClick }: { label: string; value: number; icon: React.ReactNode; color: string; onClick?: () => void }) {
  return (
    <Card withBorder padding="md" radius="md" style={{ cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{label}</Text>
          <Text fz={26} fw={700}>{value}</Text>
        </Stack>
        <ThemeIcon variant="light" color={color} size="lg">{icon}</ThemeIcon>
      </Group>
    </Card>
  );
}

export default function AssetStaffDashboard() {
  const assetService = useAssetService();
  const scanService = useAssetScanService();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, hasAnyRole } = useEnrichedAuth();
  const myId = user?.id;
  const isAdmin = hasAnyRole(ASSET_ADMIN_ROLES);

  const [scanCode, setScanCode] = useState('');

  const { data: myAssets = [], isLoading, isFetching } = useQuery({
    queryKey: assetKeys.myList(myId),
    queryFn: () => assetService.listView({ ownerUserId: myId }),
    enabled: !!myId,
  });

  // Best-effort, capped fan-out for "transfers awaiting my acknowledgement".
  const { data: pending = [] } = useQuery({
    queryKey: assetKeys.pendingAcks(myId),
    enabled: !!myId && ASSET_FLAGS.pendingAckFanOut && myAssets.length > 0,
    queryFn: async () => {
      const subset = myAssets.slice(0, ASSET_FLAGS.pendingAckMaxAssets);
      const results = await Promise.all(
        subset.map(async (a) => {
          try {
            const asg = await assetService.getAssignments(a.assetID!);
            const cur = asg.find((x) => x.isCurrent);
            return cur && !cur.acknowledgedByUserId ? { asset: a, assignment: cur } : null;
          } catch { return null; }
        }),
      );
      return results.filter(Boolean) as { asset: AssetModelView; assignment: AssetAssignmentModelView }[];
    },
  });

  const ackMutation = useMutation({
    mutationFn: (assignmentId: number) => assetService.acknowledgeTransfer(assignmentId),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: assetKeys.all }); toast.success('Transfer acknowledged'); },
    onError: (e: Error) => toast.error('Acknowledge failed', e.message),
  });

  const scanMutation = useMutation({
    mutationFn: (code: string) => scanService.resolve({ scannedCode: code.trim() }),
    onSuccess: (res) => {
      if (res?.resolved && res.asset?.assetID) { setScanCode(''); navigate(`/app/assetmanagement/view/${res.asset.assetID}`); }
      else toast.error('No match', res?.message ?? 'No asset matched the scanned code');
    },
    onError: (e: Error) => toast.error('Scan failed', e.message),
  });

  const counts = useMemo(() => ({
    total: myAssets.length,
    inService: myAssets.filter((a) => a.assetStatusID === 1).length,
    maintenance: myAssets.filter((a) => a.assetStatusID === 3).length,
    pending: pending.length,
  }), [myAssets, pending]);

  if (isLoading) return <Center h={400}><Loader size="lg" /></Center>;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Stack gap={0}>
          <Group gap="sm"><IconBox size={28} /><Title order={2}>My Assets</Title></Group>
          <Text size="sm" c="dimmed">{counts.total} {counts.total === 1 ? 'asset' : 'assets'} assigned to you</Text>
        </Stack>
        <Group gap="sm">
          {isAdmin && (
            <Button variant="default" leftSection={<IconLayoutDashboard size={16} />} onClick={() => navigate('/app/assetmanagement/admin/overview')}>
              Admin overview
            </Button>
          )}
          <Tooltip label="Refresh">
            <ActionIcon variant="subtle" loading={isFetching} onClick={() => queryClient.invalidateQueries({ queryKey: assetKeys.all })}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          <Button variant="default" leftSection={<IconScan size={16} />} onClick={() => navigate('/app/assetmanagement/scan')}>Scan to Find</Button>
        </Group>
      </Group>

      <FadeIn>
        <SimpleGrid cols={{ base: 2, md: 4 }}>
          <StatCard label="My assets" value={counts.total} icon={<IconBox size={18} />} color="blue" onClick={() => navigate('/app/assetmanagement/my-assets')} />
          <StatCard label="In service" value={counts.inService} icon={<IconCircleCheck size={18} />} color="teal" onClick={() => navigate('/app/assetmanagement/my-assets')} />
          <StatCard label="Under maintenance" value={counts.maintenance} icon={<IconTool size={18} />} color="orange" onClick={() => navigate('/app/assetmanagement/my-assets')} />
          <StatCard label="Pending acknowledgement" value={counts.pending} icon={<IconBellRinging size={18} />} color="grape" onClick={() => navigate('/app/assetmanagement/my-assets?tab=pending')} />
        </SimpleGrid>
      </FadeIn>

      {/* Scan-to-find */}
      <Card withBorder padding="lg" radius="md">
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Stack gap={2} style={{ flex: 1, minWidth: 240 }}>
            <Text fw={600}>Scan to find an asset</Text>
            <form onSubmit={(e) => { e.preventDefault(); if (scanCode.trim()) scanMutation.mutate(scanCode); }}>
              <Group gap="sm">
                <TextInput placeholder="Scan or enter RFID / QR / barcode / serial…" leftSection={<IconScan size={16} />} value={scanCode} onChange={(e) => setScanCode(e.currentTarget.value)} style={{ flex: 1 }} />
                <Button type="submit" loading={scanMutation.isPending}>Find</Button>
              </Group>
            </form>
          </Stack>
        </Group>
      </Card>

      {/* Awaiting my acknowledgement */}
      {ASSET_FLAGS.pendingAckFanOut && (
        <Card withBorder padding="lg" radius="md">
          <Text fw={600} mb="sm">Awaiting your acknowledgement</Text>
          {pending.length === 0 ? (
            <EmptyState icon={<IconBellRinging size={40} />} title="All caught up" description="No transfers are awaiting your acknowledgement." size="sm" />
          ) : (
            <Stack gap="xs">
              {pending.map(({ asset, assignment }) => (
                <Group key={assignment.assignmentID} justify="space-between" wrap="nowrap">
                  <Group gap="sm" style={{ cursor: 'pointer' }} onClick={() => navigate(`/app/assetmanagement/view/${asset.assetID}`)}>
                    <AssetStatusBadge statusId={asset.assetStatusID} statusName={asset.statusName} statusColor={asset.statusColor} />
                    <div>
                      <Text size="sm" fw={500}>{asset.name}</Text>
                      <Text size="xs" c="dimmed">{asset.assetCode} · from {assignment.transferReason ?? 'transfer'}</Text>
                    </div>
                  </Group>
                  <Button size="xs" loading={ackMutation.isPending} onClick={() => assignment.assignmentID && ackMutation.mutate(assignment.assignmentID)}>
                    Acknowledge
                  </Button>
                </Group>
              ))}
            </Stack>
          )}
        </Card>
      )}

      {/* My recent assets */}
      <Card withBorder padding="lg" radius="md">
        <Group justify="space-between" mb="sm">
          <Text fw={600}>My assets</Text>
          <Button variant="subtle" size="xs" rightSection={<IconArrowRight size={14} />} onClick={() => navigate('/app/assetmanagement/my-assets')}>View all</Button>
        </Group>
        {myAssets.length === 0 ? (
          <EmptyState icon={<IconBox size={40} />} title="No assets assigned" description="Assets assigned to you will appear here." size="sm" />
        ) : (
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            <Stack gap={0}>
              {myAssets.slice(0, 12).map((a, i) => (
                <div key={a.assetID}>
                  {i > 0 && <Divider />}
                  <Group justify="space-between" py="xs" style={{ cursor: 'pointer' }} onClick={() => navigate(`/app/assetmanagement/view/${a.assetID}`)}>
                    <Group gap="sm">
                      <AssetStatusBadge statusId={a.assetStatusID} statusName={a.statusName} statusColor={a.statusColor} />
                      <div>
                        <Text size="sm" fw={500}>{a.name}</Text>
                        <Text size="xs" c="dimmed">{a.assetCode}{a.currentLocationName ? ` · ${a.currentLocationName}` : ''}</Text>
                      </div>
                    </Group>
                    {a.categoryName && <Badge variant="light" color="gray" size="sm">{a.categoryName}</Badge>}
                  </Group>
                </div>
              ))}
            </Stack>
          </div>
        )}
      </Card>
    </Stack>
  );
}
